import { useState, useEffect } from 'react';
import type { AgentApiJobSourcesService } from '@/types/serializers';
import { Button } from '@/components/Button';
import Input from '@/components/Input';
import usePostJobSources from '@/features/agent/linked_accounts/api/usePostJobSources';
import useFetchJobSource from '@/features/agent/linked_accounts/api/useFetchJobSource';
import { showErrorToast, showSuccessToast } from '@/features/agent/job_change_processes/lib/toast';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import Loader from './Loader';

type Props = {
  service: AgentApiJobSourcesService;
  onComplete: () => void;
  onLoadingChange?: (isLoading: boolean) => void;
};

export default function TrialAccountLinkStep3({ service, onComplete, onLoadingChange }: Props) {
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [accessUrl, setAccessUrl] = useState('');
  const [urlsText, setUrlsText] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [createdJobSourceId, setCreatedJobSourceId] = useState<number | null>(null);

  const { mutateAsync: createJobSource, isPending } = usePostJobSources();

  const { data: jobSourceData } = useFetchJobSource(createdJobSourceId, {
    pollUntilCollectionComplete: true,
    onCollectionComplete: () => {
      showSuccessToast('求人の取り込みが完了しました');
      onComplete();
    },
    onLoginFailed: () => {
      showErrorToast('ログインに失敗しました。IDとパスワードを確認してください。');
      setCreatedJobSourceId(null);
    },
  });

  const isIdPasswordType =
    service.auth_type === 'ID_PASSWORD' || service.auth_type === 'ID_PASSWORD_CONDITIONS';
  const isIdPasswordUrlType = service.auth_type === 'ID_PASSWORD_URL';
  const isUrlType = service.auth_type === 'URL' || service.auth_type === 'AGENT_URL';
  const requiresLoginInfo = isIdPasswordType || isIdPasswordUrlType;

  let isFormValid = false;
  if (isUrlType) {
    isFormValid = urlsText.trim() !== '';
  } else if (isIdPasswordUrlType) {
    isFormValid = !!accessUrl.trim() && !!loginId && !!password;
  } else if (isIdPasswordType) {
    isFormValid = !!loginId && !!password;
  }

  const isProcessing =
    isPending || !!(createdJobSourceId && jobSourceData?.collection_status !== 'success');

  // ローディング状態を親に通知
  const isLoading = isPending || !!createdJobSourceId;
  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid || isProcessing) return;

    try {
      let result;

      if (isUrlType) {
        // URL/AGENT_URLタイプ: 複数URL配列で送信
        const urlArray = urlsText
          .split('\n')
          .map((url) => url.trim())
          .filter((url) => url !== '');

        result = await createJobSource({
          source_type: service.source_type,
          service_type: 'normal',
          urls: urlArray,
        });
      } else {
        // ID_PASSWORD系タイプ: ログイン情報で送信
        result = await createJobSource({
          source_type: service.source_type,
          service_type: 'normal',
          login_id: loginId,
          login_pass: password,
          ...(isIdPasswordUrlType && { access_url: accessUrl }),
        });
      }

      if (result && result.id) {
        setCreatedJobSourceId(result.id);
        showSuccessToast('アカウント連携を開始しました');
      }
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'アカウント連携に失敗しました');
    }
  };

  // 取り込み準備中（POST送信中）
  if (isPending) {
    return (
      <div className="flex h-[526px] w-[940px] flex-col items-center justify-center gap-[72px]">
        {/* タイトルとサブテキスト */}
        <div className="flex w-full flex-col items-center gap-6">
          <div className="flex flex-col items-center gap-3">
            <h1 className="text-center text-3xl leading-[1.4] font-bold text-black">
              取り込みの準備をしています
            </h1>
            <p className="text-center text-base leading-[1.7] text-black">
              取り込みが開始されるまで、しばらくお待ちください。
            </p>
          </div>
        </div>

        {/* ローディングアニメーション */}
        <div className="flex h-[97px] w-[360px] items-center justify-center">
          <Loader />
        </div>

        {/* ボタン */}
        <div className="flex w-[360px] flex-col items-stretch justify-center gap-4">
          <Button type="button" color="primary" size="lg" disabled className="h-[52px] w-full">
            無料で始める
          </Button>
        </div>
      </div>
    );
  }

  // 取り込み中（POST成功後）
  if (createdJobSourceId) {
    return (
      <div className="flex h-[526px] w-[940px] flex-col items-center justify-center gap-[72px]">
        {/* タイトルとサブテキスト */}
        <div className="flex w-full flex-col items-center gap-6">
          <div className="flex flex-col items-center gap-3">
            <h1 className="text-center text-3xl leading-[1.4] font-bold text-black">
              取り込みが開始されました
            </h1>
            <p className="text-center text-base leading-[1.7] text-black">
              トライアル版をご使用いただく、準備ができました。
            </p>
          </div>
        </div>

        {/* ローディングアニメーション */}
        <div className="flex h-[97px] w-[360px] items-center justify-center">
          <Loader />
        </div>

        <div className="flex w-[360px] flex-col items-stretch justify-center gap-4">
          <Button
            type="button"
            color="primary"
            size="lg"
            onClick={onComplete}
            className="h-[52px] w-full"
          >
            無料で始める
          </Button>
        </div>
      </div>
    );
  }

  // フォーム入力画面
  return (
    <div className="flex w-full max-w-[940px] flex-col items-center gap-x3l">
      {/* タイトル */}
      <div className="flex flex-col items-center gap-sm">
        <h1 className="text-center text-heading-xl text-black">
          {isUrlType
            ? service.auth_type === 'AGENT_URL'
              ? `${service.name}のURLを改行して入力`
              : 'URLを改行区切りで入力'
            : `${service.name} のログイン情報を入力`}
        </h1>
        <div className="flex items-center gap-1">
          <p className="text-center text-body-md text-black">
            {isUrlType
              ? service.auth_type === 'AGENT_URL'
                ? `${service.name}のエージェント向けURLを入力することで、求人を取り込むことができます。`
                : '公開求人のURLを入力することで、求人を取り込むことができます。'
              : 'ログインして求人を取り込むことができます。'}
          </p>
        </div>
      </div>

      {/* フォーム */}
      <form onSubmit={handleSubmit} className="flex w-full max-w-[480px] flex-col gap-lg">
        {/* URL複数入力 (URL/AGENT_URLタイプの場合) */}
        {isUrlType && (
          <div className="flex flex-col gap-xs">
            <textarea
              className="h-[120px] w-full resize-none appearance-none rounded border border-solid border-border p-3 text-sm leading-[150%]"
              placeholder={service.placeholder || 'URLを改行区切りで入力してください'}
              value={urlsText}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setUrlsText(e.target.value)}
              disabled={isProcessing}
            />
          </div>
        )}

        {/* 単一URL入力 (ID_PASSWORD_URLタイプの場合) */}
        {isIdPasswordUrlType && (
          <div className="flex flex-col gap-xs">
            <label htmlFor="accessUrl" className="text-heading-xxs">
              検索結果URL
            </label>
            <div className="flex flex-col gap-1">
              <Input
                id="accessUrl"
                type="url"
                value={accessUrl}
                onChange={(e) => setAccessUrl(e.target.value)}
                placeholder="https://"
                disabled={!!isProcessing}
                className="w-full"
              />
            </div>
          </div>
        )}

        {/* ログイン情報入力 (ID_PASSWORDまたはID_PASSWORD_CONDITIONSまたはID_PASSWORD_URLタイプの場合) */}
        {requiresLoginInfo && (
          <>
            {/* ログインID */}
            <div className="flex flex-col gap-xs">
              <label htmlFor="loginId" className="text-heading-xxs">
                ログインID
              </label>
              <div className="flex flex-col gap-1">
                <Input
                  id="loginId"
                  type="text"
                  value={loginId}
                  onChange={(e) => setLoginId(e.target.value)}
                  placeholder="ログインID"
                  disabled={!!isProcessing}
                  className="w-full"
                />
              </div>
            </div>

            {/* ログインパスワード */}
            <div className="flex flex-col gap-xs">
              <label htmlFor="password" className="text-heading-xxs">
                ログインパスワード
              </label>
              <div className="flex flex-col gap-xxs">
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="パスワード"
                    disabled={!!isProcessing}
                    className="w-full"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute top-1/2 right-3 -translate-y-1/2 text-[#6D6D6D] hover:text-[#262626]"
                    disabled={!!isProcessing}
                  >
                    {showPassword ? (
                      <MdVisibility className="size-6 text-sub" />
                    ) : (
                      <MdVisibilityOff className="size-6 text-sub" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* 送信ボタン */}
        <div className="mt-lg flex justify-center">
          <Button
            type="submit"
            color="primary"
            size="lg"
            disabled={!isFormValid || !!isProcessing}
            className="h-[52px] w-[320px]"
          >
            {isProcessing ? '取り込み中...' : '求人を取り込み同期する'}
          </Button>
        </div>
      </form>
    </div>
  );
}
